import React, { createContext, useContext, useState } from "react";
import axios from "axios";

const ExpenseInvoiceContext = createContext();

export const useExpenseInvoiceContext = () => {
  const context = useContext(ExpenseInvoiceContext);
  if (!context) {
    throw new Error(
      "useExpenseInvoiceContext must be used within an ExpenseInvoiceProvider"
    );
  }
  return context;
};

export const ExpenseInvoiceProvider = ({ children }) => {
  const [expenseInvoices, setExpenseInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // جلب جميع فواتير الصرف
  const fetchExpenseInvoices = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/expenseInvoice/getExpenseInvoices.php`,
        {
          withCredentials: true,
        }
      );
      if (response.data.status === "success") {
        setExpenseInvoices(response.data.invoices);
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError("فشل في جلب فواتير الصرف");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // جلب فاتورة صرف واحدة
  const fetchExpenseInvoice = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/expenseInvoice/getExpenseInvoice.php?id=${id}`,
        {
          withCredentials: true,
        }
      );
      if (response.data.status === "success") {
        return response.data.invoice;
      } else {
        setError(response.data.message);
        return null;
      }
    } catch (err) {
      setError("فشل في جلب فاتورة الصرف");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // إضافة فاتورة صرف جديدة
  const createExpenseInvoice = async (invoiceData) => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/expenseInvoice/newExpenseInvoice.php`,
        invoiceData,
        {
          withCredentials: true,
        }
      );
      if (response.data.status === "success") {
        setSuccess(response.data.message);
        return response.data;
      } else {
        setError(response.data.message);
        throw new Error(response.data.message);
      }
    } catch (err) {
      setError(err.response?.data?.message || "فشل في إضافة فاتورة الصرف");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // تحديث فاتورة صرف
  const updateExpenseInvoice = async (invoiceData) => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/expenseInvoice/updateExpenseInvoice.php`,
        invoiceData,
        {
          withCredentials: true,
        }
      );
      if (response.data.status === "success") {
        setSuccess(response.data.message);
        return response.data;
      } else {
        setError(response.data.message);
        throw new Error(response.data.message);
      }
    } catch (err) {
      setError(err.response?.data?.message || "فشل في تحديث فاتورة الصرف");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // حذف فاتورة صرف
  const deleteExpenseInvoice = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/expenseInvoice/deleteExpenseInvoice.php`,
        { id },
        {
          withCredentials: true,
        }
      );
      if (response.data.status === "success") {
        return response.data;
      } else {
        setError(response.data.message);
        throw new Error(response.data.message);
      }
    } catch (err) {
      setError(err.response?.data?.message || "فشل في حذف فاتورة الصرف");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    expenseInvoices,
    loading,
    error,
    success,
    fetchExpenseInvoices,
    fetchExpenseInvoice,
    createExpenseInvoice,
    updateExpenseInvoice,
    deleteExpenseInvoice,
  };

  return (
    <ExpenseInvoiceContext.Provider value={value}>
      {children}
    </ExpenseInvoiceContext.Provider>
  );
};
